import React from 'react';
import './Newborns.css';

import 'bootstrap/dist/css/bootstrap.min.css';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import translationsJson from 'data/translations.json';

import WordCloud from 'components/Wordcloud/Wordcloud';
import Ranking from 'components/Ranking/Ranking';
import Chart from 'components/Chart/Chart';
import rankingNamesJson from 'data/newbornNameRanking.json';
import rankingNamesGenerationJson from 'data/newbornNameRankingGenerations.json';

import Searchbar from 'components/Searchbar/Searchbar';
import nameIndices from 'data/newbornNameIndex.json';

import { WordCloudEntry, Person, PersonFileInfo, Sex, NewbornRankingData, SexSelection, LanguageGroup, RankingEntry, RankingData, DownloadType } from 'lib/Types';

import info from 'data/info.json';
import 'general.css';
import GenderFilter from 'components/GenderFilter/GenderFilter';
import LanguageFilter from 'components/LanguageFilter/LanguageFilter';
import GenerationFilter from 'components/GenerationFilter/GenerationFilter';
import AnimatedRanking from 'components/Ranking/AnimatedRanking';
import Annotation from 'components/Annotation/Annotation';
import DownloadSection from 'components/DownloadSection/DownloadSection';
import Indication from 'components/Indication/Indication';

import withRouter from 'lib/withRouter';
import { NavigateFunction } from 'react-router-dom';
import { MobileDeviceContext } from 'lib/Screen';

const translations: Translations = translationsJson;
const rankingNames: NewbornRankingData = rankingNamesJson;
const rankingNamesGeneration: NewbornRankingData = rankingNamesGenerationJson;

enum TabNames {
    Wordcloud = 0,
    Ranking = 1,
    GenerationRanking = 2,
}

interface Props { 
	params: Record<string, string>;
	navigate: NavigateFunction;
}

interface State { 
    tabKey: string;
    currentYear: number;
    valuesForChart: Person[];
    sexSelection: SexSelection[];
    language: LanguageGroup[];
    generation: string[];
}

class Newborns extends React.Component<Props, State> {
    static contextType = MobileDeviceContext;

    private wordCloudNames: WordCloudEntry[];
    private availableYears: number[];
    private defaultChartValues: Person[];

    constructor(props: Props) {
        super(props);

        const years = Object.keys(rankingNames).sort();
        this.availableYears = years.map(year => Number(year));

        this.wordCloudNames = [];
        this.generateWordCloudNames(LanguageGroup.All, SexSelection.All, GenerationFilter.DefaultValue);
        const rankingData = rankingNames[years[years.length - 1]][LanguageGroup.All];

        const topRankingMale = rankingData[Sex.Male][0] ? this.nameEntryToType(rankingData[Sex.Male][0], Sex.Male) : null;
        const topRankingFemale = rankingData[Sex.Female][0] ? this.nameEntryToType(rankingData[Sex.Female][0], Sex.Female) : null;

        this.defaultChartValues = [];
        if (topRankingMale)
            this.defaultChartValues.push({ name: topRankingMale.text, sex: topRankingMale.sex })
        if (topRankingFemale)
            this.defaultChartValues.push({ name: topRankingFemale.text, sex: topRankingFemale.sex })

        this.state = {
            tabKey: 'tab' in this.props.params ? this.props.params['tab'] : "wordcloud",
            currentYear: this.availableYears[this.availableYears.length - 1],
            valuesForChart: this.defaultChartValues,
            sexSelection: [SexSelection.All, SexSelection.All, SexSelection.All],
            language: [LanguageGroup.All, LanguageGroup.All, LanguageGroup.All],
            generation: [GenerationFilter.DefaultValue, GenerationFilter.DefaultValue, GenerationFilter.DefaultValue]
        }

        this.onUpdate = this.onUpdate.bind(this);
        this.onUpdateSelectedSex = this.onUpdateSelectedSex.bind(this);
        this.onUpdateLanguage = this.onUpdateLanguage.bind(this);
        this.onUpdateGeneration = this.onUpdateGeneration.bind(this);
        this.generateWordCloudNames = this.generateWordCloudNames.bind(this);
        this.onChartSearch = this.onChartSearch.bind(this);
        this.onChartItemRemove = this.onChartItemRemove.bind(this);
        this.onNameSelected = this.onNameSelected.bind(this);
        this.nameEntryToType = this.nameEntryToType.bind(this);
    }

    private onUpdate(newValue: number) {
        this.setState({ currentYear: newValue });
    }

    private nameEntryToType(entry: RankingEntry, sex: Sex) {
        return { text: entry.name, value: entry.count, sex: sex };
    };

    private generateWordCloudNames(language: LanguageGroup, sex: SexSelection, generation: string) {
        let rankingData: RankingData;

        if (generation === GenerationFilter.DefaultValue) {
            const years = Object.keys(rankingNames).sort();
            rankingData = rankingNames[years[years.length - 1]][language];
        } else {
            rankingData = rankingNamesGeneration[generation][language];
        }

        if (sex === SexSelection.All) {
            const maleData = rankingData.m.slice(0, Math.min(rankingData.m.length, info.wordCloudNameCount / 2)).map(item => this.nameEntryToType(item, Sex.Male));
            const femaleData = rankingData.f.slice(0, Math.min(rankingData.f.length, info.wordCloudNameCount / 2)).map(item => this.nameEntryToType(item, Sex.Female));
            this.wordCloudNames = maleData.concat(femaleData);
        } else if (sex === SexSelection.Male) {
            this.wordCloudNames = rankingData.m.slice(0, Math.min(rankingData.m.length, info.wordCloudNameCount)).map(item => this.nameEntryToType(item, Sex.Male));
        } else {
            this.wordCloudNames = rankingData.f.slice(0, Math.min(rankingData.f.length, info.wordCloudNameCount)).map(item => this.nameEntryToType(item, Sex.Female));
        }
    }

    private onUpdateSelectedSex(sex: SexSelection, index: number) {
        if (index === TabNames.Wordcloud) {
            this.generateWordCloudNames(this.state.language[index], sex, this.state.generation[index]);
        }
        const selection = this.state.sexSelection;
        selection[index] = sex;
        this.setState({ sexSelection: selection });
    }

    private onUpdateLanguage(newLanguage: LanguageGroup, index: number) {
        if (index === TabNames.Wordcloud) {
            this.generateWordCloudNames(newLanguage, this.state.sexSelection[index], this.state.generation[index]);
        }
        const language = this.state.language;
        language[index] = newLanguage;
        this.setState({ language: language });
    }

    private onUpdateGeneration(newGeneration: string, index: number) {
        if (index === TabNames.Wordcloud) {
            this.generateWordCloudNames(this.state.language[index], this.state.sexSelection[index], newGeneration);
        }
        const generation = this.state.generation;
        generation[index] = newGeneration;
        this.setState({ generation: generation });
    }

    private onChartSearch(searchedPerson: PersonFileInfo, searchedPeople: PersonFileInfo[]) {
        this.setState({ valuesForChart: searchedPeople.map(item => ({ name: item.person.name, sex: item.person.sex })) });
    }

    private onChartItemRemove(removedPerson: PersonFileInfo, searchedPeople: PersonFileInfo[]) {
        this.setState({ valuesForChart: searchedPeople.map(item => ({ name: item.person.name, sex: item.person.sex })) });
    }

    private onNameSelected(name: string, sex: Sex | undefined) {
        let found = false;
        for (let p of this.state.valuesForChart) {
            if (p.name === name && p.sex === sex) {
                found = true;
                break;
            }
        }
        if (!found) {
            let newChartValues = [...this.state.valuesForChart]
            newChartValues.push({ name: name, sex: sex });
            this.setState({ tabKey: "graph", valuesForChart: newChartValues });
        } else {
            this.setState({ tabKey: "graph" });
        }

        this.props.navigate("graph");
    }

    private renderWordCloud() {
        return (
            <>
                <div className="filter-div">
                    <GenderFilter onUpdate={(e) => { this.onUpdateSelectedSex(e, TabNames.Wordcloud) }} young={true} />
                    <LanguageFilter onUpdate={(e) => { this.onUpdateLanguage(e, TabNames.Wordcloud) }} />
                    <GenerationFilter onUpdate={(e: string) => { this.onUpdateGeneration(e, TabNames.Wordcloud) }} allowAll={true} />
                </div>
                <WordCloud words={this.wordCloudNames} nameSelected={this.onNameSelected} />
            </>
        );
    }

    private renderRanking() {
        return (
            <AnimatedRanking names={rankingNames} title="" nameSelected={this.onNameSelected} />
        );
    }

    private renderGraph() {
        return (
            <div className='linechart-container'>
                <Searchbar isMultiSearch={true} placeholder={translations.name[window.lang]} inputData={nameIndices} onSearch={this.onChartSearch} onItemRemoved={this.onChartItemRemove} presetValues={this.state.valuesForChart} />
                <Chart persons={this.state.valuesForChart} />
            </div>
        );
    }

    private renderGenerationRanking() {
        const generation = this.state.generation[TabNames.GenerationRanking] === GenerationFilter.DefaultValue ? info.generations[0].name : this.state.generation[TabNames.GenerationRanking];
        const rankingDataMaleGeneration = rankingNamesGeneration[generation][this.state.language[TabNames.GenerationRanking]].m;
        const rankingDataFemaleGeneration = rankingNamesGeneration[generation][this.state.language[TabNames.GenerationRanking]].f;

        return (
            <>
                <div className="filter-div">
                    <GenerationFilter onUpdate={(e: string) => { this.onUpdateGeneration(e, TabNames.GenerationRanking) }} allowAll={false} />
                    <LanguageFilter onUpdate={(e) => { this.onUpdateLanguage(e, TabNames.GenerationRanking) }} />
                </div>
                <div className="side-by-side">
                    {(rankingDataMaleGeneration.length) ?
                        <Ranking names={rankingDataMaleGeneration.slice(0, info.newbornNameRankingCount)} title={`${translations.newborns[window.lang]} ${translations[generation][window.lang]}`} sex={Sex.Male} nameSelected={this.onNameSelected}></Ranking>
                        : ""
                    }

                    {(rankingDataFemaleGeneration.length) ?
                        <Ranking names={rankingDataFemaleGeneration.slice(0, info.newbornNameRankingCount)} title={`${translations.newborns[window.lang]} ${translations[generation][window.lang]}`} sex={Sex.Female} nameSelected={this.onNameSelected}></Ranking>
                        : ""
                    }
                </div>
            </>
        );
    }

    componentDidUpdate(prevProps: Props) {
        const prevTab = 'tab' in prevProps.params ? prevProps.params['tab'] : null;
        const tab = 'tab' in this.props.params ? this.props.params['tab'] : null;
        if (tab && prevTab !== tab)
            this.setState({ tabKey: tab })
    }

    render() {
        let content: JSX.Element;

        if (this.context) {
            switch (this.state.tabKey) {
                case 'wordcloud':
                    content = this.renderWordCloud();
                    break;
                case 'ranking':
                    content = this.renderRanking();
                    break;
                case 'graph':
                    content = this.renderGraph();
                    break;
                case 'rankingGenerations':
                    content = this.renderGenerationRanking();
                    break;
                default:
                    content = <h3>404 Not Found</h3>
                    break;
            }
        } else {
            content = (
                <Tabs defaultActiveKey="wordcloud" className="mb-3" activeKey={this.state.tabKey} onSelect={(key) => this.setState({ tabKey: key! })}>
                    <Tab eventKey="wordcloud" title={translations.wordcloud[window.lang]}>
                        {this.renderWordCloud()}
                    </Tab>
                    <Tab eventKey="ranking" title={translations.ranking[window.lang]}>
                        {this.renderRanking()}
                    </Tab>
                    <Tab eventKey="graph" title={translations.graph[window.lang]}>
                        {this.renderGraph()}
                    </Tab>
                    <Tab eventKey="rankingGenerations" title={translations.rankingGenerations[window.lang]}>
                        {this.renderGenerationRanking()}
                    </Tab>
                </Tabs>
            );
        }

        return (
            <>
                {content}

                <Annotation page='newborns' tab={this.state.tabKey} />
                <DownloadSection type={DownloadType.Newborns} />
                <Indication />
            </>
        );
    }
}

export default withRouter(Newborns);